import { modelState, ModelStateList, modelStateListSubscriptionManager } from "@/utils/modelState";
import { addDays, isBefore, startOfDay } from 'date-fns';
import { calculateRowPrice } from "./model.Reservations";
import { ReservationState } from './model.roomsAndTotals';
import { RoomBlockDate, RoomBlockDateRoom } from './model.saveAndHistory';
import type { RoomBookingState } from "./types";
export function hydrateRoomBlockDatesContainer(reservationState: ReservationState) {
    const signal = () => {
        reservationState.RoomBlocks.Version.setValue(reservationState.RoomBlocks.Version.value + 1);
    };
    const outerSubscription = reservationState.RoomBlocks.Dates.subscribe(() => {
        signal();
    });
    const innerSubscription = modelStateListSubscriptionManager(reservationState.RoomBlocks.Dates, (date) => {
        return hydrateRoomBlockDate(date, signal);
    });
    // Subscribe to rooms
    function removeRoomBooking(room: RoomBookingState) {
        reservationState.RoomBlocks.Dates.toArray().forEach(date => {
            date.Rooms.toArray().forEach(r => {
                if (r.ReservationRoomId === room.ReservationRoomId) {
                    date.Rooms.remove(r);
                }
            });
        });
    }
    function refreshRoomBooking(room: RoomBookingState) {
        removeRoomBooking(room);
        if (room.NoShow.value) {
            return;
        }
        const start = room.CheckIn.value;
        const end = room.CheckOut.value;
        const endDate = startOfDay(end);
        for (let date = startOfDay(start); isBefore(date, addDays(endDate, 1)) || date.getTime() === endDate.getTime(); date = addDays(date, 1)) {
            const dateKey = startOfDay(date);
            let roomBlockDate = reservationState.RoomBlocks.Dates.toArray().find(d => d.Date.getTime() === dateKey.getTime());
            if (!roomBlockDate) {
                roomBlockDate = {
                    Date: date,
                    Rooms: new ModelStateList([]),
                    Version: modelState(0)
                };
                reservationState.RoomBlocks.Dates.add(roomBlockDate);
            }
            const dateTime = date;
            const isAllDay = start < dateTime && end >= addDays(dateTime, 1);
            let roomLocationId = room.RoomLocationId.value;
            if (!roomLocationId || roomLocationId === "unassigned") {
                const roomTypeDefinition = reservationState.RoomTypes[room.RoomType.value];
                if (roomTypeDefinition) {
                    roomLocationId = roomTypeDefinition.RoomTypeLocationId;
                }
            }
            const roomBlockDateRoom: RoomBlockDateRoom = {
                LocationId: roomLocationId,
                IsAllDay: isAllDay,
                Start: start < dateTime ? dateTime : start,
                End: end >= addDays(dateTime, 1) ? addDays(dateTime, 1) : end,
                ReservationRoomId: room.ReservationRoomId,
            };
            roomBlockDate.Rooms.add(roomBlockDateRoom);
        }
    }
    const roomOuterSubscription = reservationState.RoomBookings.subscribe((item) => {
        if (item.action === "remove") {
            removeRoomBooking(item.item);
        }
        else if (item.action === "reset") {
            reservationState.RoomBlocks.Dates.undo();
            reservationState.RoomBlocks.Dates.toArray().forEach(date => {
                date.Rooms.undo();
            });
            signal();
        }
        else if (item.action === "add") {
            refreshRoomBooking(item.item);
        }
    });
    const roomSubscriptions = modelStateListSubscriptionManager(reservationState.RoomBookings, (roomBooking) => {
        const subscriptions = [
            roomBooking.RoomType.subscribe(() => refreshRoomBooking(roomBooking)),
            roomBooking.RoomLocationId.subscribe(() => refreshRoomBooking(roomBooking)),
            roomBooking.CheckIn.subscribe(() => refreshRoomBooking(roomBooking)),
            roomBooking.CheckOut.subscribe(() => refreshRoomBooking(roomBooking)),
            roomBooking.NoShow.subscribe(() => refreshRoomBooking(roomBooking)),
            roomBooking.NoShowVersion.subscribe(() => refreshRoomBooking(roomBooking)),
            roomBooking.NoCharge.subscribe(() => refreshRoomBooking(roomBooking)),
            roomBooking.NoChargeVersion.subscribe(() => refreshRoomBooking(roomBooking))
        ];
        refreshRoomBooking(roomBooking);
        return () => {
            subscriptions.forEach(s => s());
        };
    });
    reservationState.RoomBookings.toArray().forEach(roomBooking => {
        refreshRoomBooking(roomBooking);
    });
    return () => {
        outerSubscription();
        innerSubscription();
        roomOuterSubscription();
        roomSubscriptions();
    };
}

export function hydrateRoomBlockDate(date: RoomBlockDate, signal: () => void) {
    const versionSubscription = date.Version.subscribe(signal);
    const listSubscription = date.Rooms.subscribe(() => {
        date.Version.setValue(date.Version.value + 1);
    });
    return () => {
        versionSubscription();
        listSubscription();
    };
}

export function hydratePeople(reservationState: ReservationState) {
    const roomSubscriptions = modelStateListSubscriptionManager(reservationState.RoomBookings, (roomBooking) => {
        const subscriptions = [
            modelStateListSubscriptionManager(roomBooking.People, (person) => {
                const subscriptions = [
                    person.CheckIn.subscribe(() => { roomBooking.PeopleVersion.setValue(roomBooking.PeopleVersion.value + 1); }),
                    person.CheckOut.subscribe(() => { roomBooking.PeopleVersion.setValue(roomBooking.PeopleVersion.value + 1); }),
                    person.Person.subscribe(() => { roomBooking.PeopleVersion.setValue(roomBooking.PeopleVersion.value + 1); })
                ];
                return () => {
                    subscriptions.forEach(s => s());
                };
            })
        ];
        return () => {
            subscriptions.forEach(s => s());
        };
    });
    const programSubscriptions = modelStateListSubscriptionManager(reservationState.ProgramBookings, (programBooking) => {
        const subscriptions = [
            modelStateListSubscriptionManager(programBooking.People, (person) => {
                const subscriptions = [
                    person.Person.subscribe(() => { programBooking.PeopleVersion.setValue(programBooking.PeopleVersion.value + 1); })
                ];
                return () => {
                    subscriptions.forEach(s => s());
                };
            })
        ];
        return () => {
            subscriptions.forEach(s => s());
        };
    });
    return () => {
        roomSubscriptions();
        programSubscriptions();
    };
}

export function hydrateCalculatedPrice(reservationState: ReservationState) {
    const subscriptions = [
        modelStateListSubscriptionManager(reservationState.RoomBookings, (roomBooking) => {
            const recalculatePrice = () => {
                roomBooking.CalculatedPrice.setValue(calculateRowPrice(roomBooking.Dates.map(d => d.PriceBreakdown), roomBooking.Price.price.value.baseAmount, roomBooking.Price.price.value.payableAmount, roomBooking.NoCharge.value));
            };
            const subscriptions = [
                roomBooking.Price.price.subscribe(() => {
                    recalculatePrice();
                }),
                roomBooking.NoShow.subscribe(() => {
                    recalculatePrice();
                }),
                roomBooking.NoShowVersion.subscribe(() => {
                    recalculatePrice();
                }),
                roomBooking.NoCharge.subscribe(() => {
                    recalculatePrice();
                }),
                roomBooking.NoChargeVersion.subscribe(() => {
                    recalculatePrice();
                })
            ];
            recalculatePrice();
            return () => {
                subscriptions.forEach(s => s());
            };
        }),
        modelStateListSubscriptionManager(reservationState.ProgramBookings, (programBooking) => {
            const recalculatePrice = () => {
                programBooking.CalculatedPrice.setValue(calculateRowPrice(programBooking.Dates.map(d => d.PriceBreakdown), programBooking.Price.price.value.baseAmount, programBooking.Price.price.value.payableAmount, programBooking.NoCharge.value));
            };
            const subscriptions = [
                programBooking.Price.price.subscribe(() => {
                    recalculatePrice();
                }),
                programBooking.NoShow.subscribe(() => {
                    recalculatePrice();
                }),
                programBooking.NoShowVersion.subscribe(() => {
                    recalculatePrice();
                }),
                programBooking.NoCharge.subscribe(() => {
                    recalculatePrice();
                }),
                programBooking.NoChargeVersion.subscribe(() => {
                    recalculatePrice();
                })
            ];
            recalculatePrice();
            return () => {
                subscriptions.forEach(s => s());
            };
        }),
    ];
    return () => {
        subscriptions.forEach(s => s());
    };
}

export interface ReservationPosItemDto {
    Description: string;
    Quantity: number;
    Amount: number;
    ReservationOrderId: string;
    ReservationOrderLineItemId: string;
    RevenueDate: string;
    CanRemove: boolean;
    CanChangeAmount: boolean;
    IsPackage: boolean;
    // Server-computed revenue lock verdict (lock-date boundary OR closed date).
    IsRevenueLocked: boolean;
}

export interface ReservationPosItem {
    Description: string;
    Quantity: number;
    Amount: number;
    ReservationOrderId: string;
    ReservationOrderLineItemId: string;
    RevenueDate: Date;
    IsRevenueLocked: boolean;
    CanRemove: boolean;
    CanChangeAmount: boolean;
    IsPackage: boolean;
}

export interface RatesContainerDto {
    RatePlans: RatesRatePlanDto[];
    BaseRates: RatesBaseRateDto[];
    Multipliers: RatesRateMultiplierDto[];
    Discounts: RatesRateDiscountDto[];
}

export interface RatesRatePlanDto {
    RatePlanId: string;
    RatePlanVersion: number;
    Name: string;
    VisibleInReservations: boolean;
    IsHistorical: boolean;
    CreatedAt: string;
    FormId: string | null;
    FormFields: RatePlanFormFieldDto[] | null;
}

export interface RatePlanFormFieldDto {
    FieldId: string;
    DisplayName: string;
    Options: RatePlanFormFieldOptionDto[];
}

export interface RatePlanFormFieldOptionDto {
    Value: string;
    Text: string;
}

export interface RatesBaseRateDto {
    BaseRateId: string;
    BaseRateVersion: number;
    Name: string;
    VisibleInReservations: boolean;
}

export interface RatesRateMultiplierDto {
    RateMultiplierId: string;
    RateMultiplierVersion: number;
    Name: string;
    VisibleInReservations: boolean;
}

export interface RatesRateDiscountDto {
    RateDiscountId: string;
    RateDiscountVersion: number;
    Name: string;
    VisibleInReservations: boolean;
}
